import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from "@store/hooks";
import {
  loadAllWorkspaceReadings,
  deleteReading,
  selectAllWorkspaceReadings,
  selectReadingsLoading,
  selectReadingsError,
} from "../state/workspaceReadingsSlice";
import { CreateReadingModal } from './CreateReadingModal';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import PeopleIcon from '@mui/icons-material/People';

export function WorkspaceReadingsScreen() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const readings = useAppSelector(selectAllWorkspaceReadings);
  const loading = useAppSelector(selectReadingsLoading);
  const error = useAppSelector(selectReadingsError);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    if (workspaceId) {
      dispatch(loadAllWorkspaceReadings(workspaceId));
    }
  }, [workspaceId, dispatch]);

  const handleDelete = async (readingId: string) => {
    if (!window.confirm('Are you sure you want to delete this reading? This action cannot be undone.')) {
      return;
    }

    setDeletingId(readingId);
    try {
      await dispatch(deleteReading(readingId)).unwrap();
    } catch (err) {
      console.error('Failed to delete reading:', err);
      alert('Failed to delete reading. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleView = (readingId: string) => {
    navigate(`/reading/${readingId}`);
  };

  const handleEdit = (readingId: string) => {
    // TODO: Implement edit modal
    console.log('Edit reading:', readingId);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!workspaceId) {
    return (
      <Box p={3}>
        <Alert severity="error">Workspace ID is required</Alert>
      </Box>
    );
  }

  if (loading && readings.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Workspace Readings</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
        >
          Create Reading
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {readings.length === 0 && !loading ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No readings yet
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Create your first reading assignment to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
          >
            Create Reading
          </Button>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Document</TableCell>
                <TableCell>Assigned By</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell align="center">
                  <Tooltip title="Number of users assigned">
                    <PeopleIcon fontSize="small" />
                  </Tooltip>
                </TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {readings.map((reading) => (
                <TableRow key={reading.reading_id} hover>
                  <TableCell>
                    <Typography variant="body1" fontWeight={500}>
                      {reading.reading_name}
                    </Typography>
                    {reading.description && (
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {reading.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{reading.document_name}</TableCell>
                  <TableCell>
                    {reading.assigned_by_username || 'Unknown'}
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={
                        reading.due_date &&
                        new Date(reading.due_date) < new Date()
                          ? 'error'
                          : 'text.primary'
                      }
                    >
                      {formatDate(reading.due_date)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={reading.assigned_user_count}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title={`${reading.completed_count} completed`}>
                        <Chip
                          label={reading.completed_count}
                          size="small"
                          color="success"
                        />
                      </Tooltip>
                      <Tooltip title={`${reading.in_progress_count} in progress`}>
                        <Chip
                          label={reading.in_progress_count}
                          size="small"
                          color="info"
                        />
                      </Tooltip>
                      <Tooltip title={`${reading.not_started_count} not started`}>
                        <Chip
                          label={reading.not_started_count}
                          size="small"
                          color="default"
                        />
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={reading.required ? 'Required' : 'Optional'}
                      size="small"
                      color={reading.required ? 'error' : 'default'}
                      variant={reading.required ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={() => handleView(reading.reading_id)}
                          color="primary"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(reading.reading_id)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(reading.reading_id)}
                          color="error"
                          disabled={deletingId === reading.reading_id}
                        >
                          {deletingId === reading.reading_id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CreateReadingModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        workspaceId={workspaceId}
      />
    </Box>
  );
}
